import React, { useRef } from 'react';
import { Box } from 'rebass';
import { PortalWithState } from 'react-portal';
import Button, { ButtonProps } from '../Button/Button';
import { Checkbox, Label } from '../Form';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import Icon from '../Icon/Icon';
import { ColumnConfig } from '../../hooks/useColumnConfiguration';

export type ColumnSelectorProps = {
  columnConfiguration: ColumnConfig[];
  setColumnConfiguration: (columns: ColumnConfig[]) => void;
  buttonProps?: ButtonProps;
};

const ColumnSelector = ({
  columnConfiguration,
  setColumnConfiguration,
  buttonProps,
}: ColumnSelectorProps): JSX.Element => {
  const parentRef = useRef<HTMLElement>();

  const reorder = (
    listOfColumns: ColumnConfig[],
    startIndex: number,
    endIndex: number
  ): ColumnConfig[] => {
    const result = Array.from(listOfColumns);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result as ColumnConfig[];
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const reorderedColumns = reorder(
      columnConfiguration,
      result.source.index,
      result.destination.index
    );

    setColumnConfiguration && setColumnConfiguration(reorderedColumns);
  };

  return (
    <PortalWithState node={parentRef.current} closeOnOutsideClick closeOnEsc>
      {({ openPortal, closePortal, portal, isOpen }) => (
        <DragDropContext onDragEnd={onDragEnd}>
          <Box ref={parentRef} sx={{ position: 'relative', zIndex: 2 }}>
            {portal(
              <Droppable droppableId="columns">
                {(provided) => (
                  <Box
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    bg="white"
                    padding="3"
                    maxHeight="calc(100vh - 300px)"
                    minHeight={150}
                    sx={{
                      boxShadow: 'menu',
                      position: 'absolute',
                      [parentRef.current &&
                      window.innerHeight -
                        parentRef.current.getBoundingClientRect().bottom <
                        parentRef.current.getBoundingClientRect().top
                        ? 'bottom'
                        : 'top']: '100%',
                      right: 0,
                      whiteSpace: 'nowrap',
                      overflowY: 'auto',
                    }}
                  >
                    {columnConfiguration.map((column, index) => {
                      const id = column.id;
                      return (
                        <Draggable
                          key={id ?? index}
                          draggableId={id}
                          index={index}
                        >
                          {(provided) => (
                            <Label
                              padding={1}
                              variant="forms.label"
                              sx={{ alignItems: 'center' }}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <Icon icon="draggable" />
                              <Checkbox
                                variant="forms.checkbox"
                                onChange={() => {
                                  if (!setColumnConfiguration) {
                                    return;
                                  }
                                  const newConfig = columnConfiguration.slice();
                                  newConfig[index] = {
                                    ...columnConfiguration[index],
                                    enabled:
                                      !columnConfiguration[index].enabled,
                                  };
                                  setColumnConfiguration(newConfig);
                                }}
                                checked={column.enabled}
                              />
                              {column.header}
                            </Label>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            )}
            <Button
              {...buttonProps}
              variant="primary"
              icon="three-columns"
              onClick={isOpen ? closePortal : openPortal}
              title="Select Columns"
            />
          </Box>
        </DragDropContext>
      )}
    </PortalWithState>
  );
};

export default ColumnSelector;
